import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from '@tanstack/react-router';
import client from '@/api/client';
import type { Tenant, RentEntry, Hostel } from '@/api/types';
import { Printer, Download, ArrowLeft, FileText } from 'lucide-react';
import { Select } from '@/components/ui/Select';

const fmt = (n: number) =>
  '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function monthRange(billingMonth: string) {
  const [year, month] = billingMonth.split('-').map(Number);
  const lastDay = new Date(year, month, 0).getDate();
  return {
    from: `${billingMonth}-01`,
    to: `${billingMonth}-${String(lastDay).padStart(2, '0')}`,
  };
}

function buildReceiptHtml(opts: {
  hostelName: string;
  hostelAddr: string;
  hostelPhone: string;
  tenant: Tenant;
  billingMonth: string;
  from: string;
  to: string;
  paidDate: string;
  totalDue: number;
  previousPending: number;
  paidAmount: number;
  remaining: number;
  signature: string;
  receiptNo: string;
  status: string;
  generatedAt: string;
}) {
  const { hostelName, hostelAddr, hostelPhone, tenant, billingMonth, from, to,
    paidDate, totalDue, previousPending, paidAmount, remaining,
    signature, receiptNo, status, generatedAt } = opts;

  const statusColor = status === 'PAID' ? '#065f46' : status === 'OVERDUE' ? '#991b1b' : '#92400e';
  const statusBg   = status === 'PAID' ? '#d1fae5' : status === 'OVERDUE' ? '#fee2e2' : '#fef3c7';

  return `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>Rent Receipt – ${tenant.fullName} – ${billingMonth}</title>
<style>
  @page { size: A4; margin: 18mm 20mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; font-family: Arial, sans-serif; }
  body { color: #111; font-size: 13px; }
  .center { text-align: center; }
  .hostel-name { font-size: 22px; font-weight: 700; color: #1E3A5F; }
  .sub { font-size: 12px; color: #555; margin-top: 3px; }
  .receipt-title { font-size: 16px; font-weight: 700; text-transform: uppercase;
    letter-spacing: 1px; color: #0f766e; margin-top: 12px; }
  .receipt-no { display: inline-block; margin-top: 6px; padding: 3px 14px;
    border: 1px solid #99f6e4; border-radius: 20px; background: #f0fdfa;
    font-size: 11px; color: #0f766e; font-weight: 600; }
  hr { border: none; border-top: 1px solid #e5e7eb; margin: 14px 0; }
  .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 30px; margin-bottom: 18px; }
  .field-label { font-size: 10px; color: #9ca3af; text-transform: uppercase;
    letter-spacing: 0.5px; margin-bottom: 2px; }
  .field-value { font-size: 13px; font-weight: 600; color: #111; }
  table { width: 100%; border-collapse: collapse; border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden; }
  thead tr { background: #f8fafc; }
  th { padding: 10px 16px; text-align: left; font-size: 11px; text-transform: uppercase;
    letter-spacing: 0.5px; color: #6b7280; font-weight: 600; }
  th:last-child { text-align: right; }
  td { padding: 10px 16px; font-size: 13px; border-top: 1px solid #e5e7eb; }
  td:last-child { text-align: right; font-weight: 500; }
  .row-pending td { color: #b45309; }
  .row-subtotal td { background: #f8fafc; font-weight: 600; }
  .row-paid td:last-child { color: #059669; }
  .row-remaining td { background: ${remaining > 0 ? '#fff1f2' : '#f0fdf4'};
    font-weight: 700; font-size: 14px; border-top: 2px solid ${remaining > 0 ? '#fca5a5' : '#86efac'}; }
  .row-remaining td:last-child { color: ${remaining > 0 ? '#dc2626' : '#16a34a'}; }
  .status-badge { display: inline-block; padding: 3px 12px; border-radius: 20px;
    font-size: 11px; font-weight: 700; background: ${statusBg}; color: ${statusColor}; }
  .sig-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-top: 40px; }
  .sig-box { border-bottom: 1px solid #6b7280; padding-bottom: 4px; min-height: 32px; font-size: 13px; }
  .sig-label { font-size: 10px; color: #9ca3af; margin-top: 4px; }
  .footer { margin-top: 30px; text-align: center; font-size: 11px; color: #9ca3af; }
</style></head><body>
<div class="center">
  <p class="hostel-name">${hostelName}</p>
  ${hostelAddr ? `<p class="sub">${hostelAddr}</p>` : ''}
  ${hostelPhone ? `<p class="sub">Tel: ${hostelPhone}</p>` : ''}
  <p class="receipt-title">Rent Receipt</p>
  <span class="receipt-no">${receiptNo}</span>
</div>

<hr/>

<div class="grid2">
  <div><p class="field-label">Full Name</p><p class="field-value">${tenant.fullName}</p></div>
  <div><p class="field-label">Phone</p><p class="field-value">${tenant.phone}</p></div>
  <div><p class="field-label">Room Number</p><p class="field-value">${tenant.roomNumber ?? '—'}</p></div>
  <div><p class="field-label">Bed</p><p class="field-value">${tenant.bedLabel ?? '—'}</p></div>
  <div><p class="field-label">From Date</p><p class="field-value">${from}</p></div>
  <div><p class="field-label">To Date</p><p class="field-value">${to}</p></div>
  <div><p class="field-label">Paid Date</p><p class="field-value">${paidDate}</p></div>
  <div><p class="field-label">Billing Month</p><p class="field-value">${billingMonth}</p></div>
</div>

<table>
  <thead>
    <tr><th>Description</th><th style="text-align:right">Amount</th></tr>
  </thead>
  <tbody>
    <tr>
      <td>Monthly Rent (${billingMonth})</td>
      <td>${fmt(totalDue)}</td>
    </tr>
    ${previousPending > 0 ? `<tr class="row-pending">
      <td>Previous Pending Balance (carried forward)</td>
      <td>${fmt(previousPending)}</td>
    </tr>` : ''}
    <tr class="row-subtotal">
      <td>Total Amount Due</td>
      <td>${fmt(totalDue + previousPending)}</td>
    </tr>
    <tr class="row-paid">
      <td>Amount Paid</td>
      <td>– ${fmt(paidAmount)}</td>
    </tr>
    <tr class="row-remaining">
      <td>Remaining Amount</td>
      <td>${fmt(remaining)}</td>
    </tr>
  </tbody>
</table>

<div style="margin-top:14px;display:flex;align-items:center;gap:10px">
  <span style="font-size:12px;color:#555">Payment Status:</span>
  <span class="status-badge">${status}</span>
</div>

<div class="sig-grid">
  <div>
    <p class="field-label" style="margin-bottom:8px">Tenant Signature</p>
    <div class="sig-box">${signature}</div>
    <p class="sig-label">Tenant Name &amp; Signature</p>
  </div>
  <div>
    <p class="field-label" style="margin-bottom:8px">Authorized By</p>
    <div class="sig-box"></div>
    <p class="sig-label">Manager Signature &amp; Seal</p>
  </div>
</div>

<div class="footer">
  <p>This is a computer-generated receipt &middot; ${hostelName}</p>
  <p style="margin-top:3px">Generated on ${generatedAt}</p>
</div>
</body></html>`;
}

export function TenantEncashmentPage() {
  const { t } = useTranslation();
  const { id: hostelId, tenantId } = useParams({
    from: '/protected/owner/hostels/$id/tenants/$tenantId/encashment',
  });

  const [selectedMonth, setSelectedMonth] = useState('');
  const [paidDate, setPaidDate] = useState(new Date().toISOString().split('T')[0]);
  const [signature, setSignature] = useState('');

  const { data: tenant, isLoading: tenantLoading } = useQuery<Tenant>({
    queryKey: ['tenant', tenantId],
    queryFn: () => client.get(`/api/tenants/${tenantId}`).then(r => r as unknown as Tenant),
  });

  const { data: hostel } = useQuery<Hostel>({
    queryKey: ['hostel', hostelId],
    queryFn: () => client.get(`/api/hostels/${hostelId}`).then(r => r as unknown as Hostel),
    enabled: !!hostelId,
  });

  const { data: rentEntries = [], isLoading: rentLoading } = useQuery<RentEntry[]>({
    queryKey: ['tenant-rent', tenantId],
    queryFn: () => client.get(`/api/rent/tenant/${tenantId}`).then(r => r as unknown as RentEntry[]),
  });

  const sorted = [...rentEntries].sort((a, b) => a.billingMonth.localeCompare(b.billingMonth));
  const latest = sorted[sorted.length - 1];
  const effectiveMonth = selectedMonth || latest?.billingMonth || '';
  const current = sorted.find(e => e.billingMonth === effectiveMonth);

  const previousPending = current
    ? sorted
        .filter(e => e.billingMonth < current.billingMonth && e.status !== 'PAID' && e.status !== 'WAIVED')
        .reduce((s, e) => s + (Number(e.amountDue) - Number(e.amountPaid)), 0)
    : 0;

  const totalDue   = current ? Number(current.amountDue)  : 0;
  const paidAmount = current ? Number(current.amountPaid) : 0;
  const remaining  = totalDue + previousPending - paidAmount;
  const { from, to } = effectiveMonth ? monthRange(effectiveMonth) : { from: '—', to: '—' };

  const hostelName = hostel?.name ?? '';
  const hostelAddr = [hostel?.address, hostel?.city].filter(Boolean).join(', ');
  const receiptNo  = effectiveMonth && tenantId
    ? `RENT-${effectiveMonth.replace('-', '')}-${tenantId.slice(-4).toUpperCase()}`
    : '—';

  const openPrint = () => {
    if (!tenant) return;
    const html = buildReceiptHtml({
      hostelName,
      hostelAddr,
      hostelPhone: hostel?.phone ?? '',
      tenant,
      billingMonth: effectiveMonth,
      from,
      to,
      paidDate,
      totalDue,
      previousPending,
      paidAmount,
      remaining,
      signature,
      receiptNo,
      status: current?.status ?? 'PENDING',
      generatedAt: new Date().toLocaleDateString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
      }),
    });
    const win = window.open('', '_blank', 'width=900,height=750');
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 400);
  };

  if (tenantLoading || rentLoading) {
    return <p className="text-gray-400 py-16 text-center">{t('common.loading')}</p>;
  }
  if (!tenant) {
    return <p className="text-red-500 py-16 text-center">{t('owner.tenantNotFound', 'Tenant not found.')}</p>;
  }

  const statusColors: Record<string, string> = {
    PAID:    'bg-green-100 text-green-800',
    PARTIAL: 'bg-blue-100 text-blue-800',
    OVERDUE: 'bg-red-100 text-red-800',
    PENDING: 'bg-amber-100 text-amber-800',
    WAIVED:  'bg-gray-100 text-gray-600',
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Page header */}
      <div className="flex items-center gap-3">
        <Link
          to="/owner/hostels/$id/tenants/$tenantId"
          params={{ id: hostelId, tenantId }}
          search={{ tab: 'rent' }}
          className="text-teal-600 text-sm hover:underline flex items-center gap-1"
        >
          <ArrowLeft size={14} /> {t('common.back')}
        </Link>
        <h1 className="text-xl font-bold text-gray-900 flex-1 flex items-center gap-2">
          <FileText size={18} className="text-teal-600" /> {t('owner.rentReceipt', 'Rent Receipt')}
        </h1>
        <div className="flex items-center gap-2">
          <Select
            value={effectiveMonth}
            onChange={e => setSelectedMonth(e.target.value)}
            className="w-auto"
            disabled={sorted.length === 0}
            options={
              sorted.length === 0
                ? [{ value: '', label: t('rent.noEntries') }]
                : [...sorted].reverse().map(e => ({ value: e.billingMonth, label: e.billingMonth }))
            }
          />
          <button
            onClick={openPrint}
            disabled={!current}
            className="flex items-center gap-1.5 bg-teal-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-teal-700 disabled:opacity-40"
          >
            <Printer size={14} /> {t('owner.print', 'Print')}
          </button>
          <button
            onClick={openPrint}
            disabled={!current}
            className="flex items-center gap-1.5 bg-indigo-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-40"
          >
            <Download size={14} /> {t('owner.downloadPdf', 'Download PDF')}
          </button>
        </div>
      </div>

      {sorted.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-center text-amber-700 text-sm">
          {t('owner.noRentEntriesForEncashment', 'No rent entries found for this tenant. Encashment receipt cannot be generated yet.')}
        </div>
      )}

      {/* Receipt preview */}
      {current && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          {/* Hostel header */}
          <div className="text-center border-b border-gray-200 pb-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-900">{hostelName || t('owner.hostel', 'Hostel')}</h2>
            {hostelAddr && <p className="text-gray-500 text-sm mt-1">{hostelAddr}</p>}
            {hostel?.phone && <p className="text-gray-500 text-sm">{hostel.phone}</p>}
            <p className="text-lg font-semibold text-teal-700 mt-4 uppercase tracking-wide">
              {t('owner.rentReceipt', 'Rent Receipt')}
            </p>
            <span className="inline-block mt-2 px-4 py-1 bg-teal-50 border border-teal-200 rounded-full text-xs text-teal-700 font-medium">
              {receiptNo}
            </span>
          </div>

          {/* Tenant info grid */}
          <div className="grid grid-cols-2 gap-x-10 gap-y-4 mb-6">
            {[
              [t('tenant.fullName'), tenant.fullName],
              [t('common.phone'), tenant.phone],
              [t('room.roomNumber'), tenant.roomNumber ?? '—'],
              [t('common.bed'), tenant.bedLabel ?? '—'],
              [t('owner.fromDate', 'From Date'), from],
              [t('owner.toDate', 'To Date'), to],
              [t('rent.billingMonth'), effectiveMonth],
            ].map(([label, value]) => (
              <div key={label}>
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">{label}</p>
                <p className="font-semibold text-gray-900 text-sm">{value}</p>
              </div>
            ))}
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">{t('rent.paidDate')}</p>
              <input
                type="date"
                value={paidDate}
                onChange={e => setPaidDate(e.target.value)}
                className="border border-gray-300 rounded-lg px-2 py-1 text-sm text-gray-900 w-full max-w-[160px]"
              />
            </div>
          </div>

          {/* Amount table */}
          <div className="border border-gray-200 rounded-xl overflow-hidden mb-5">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('owner.descriptionLabel', 'Description')}</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('common.amount')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <tr>
                  <td className="px-5 py-3 text-sm text-gray-700">{t('owner.monthlyRentFor', 'Monthly Rent ({{month}})', { month: effectiveMonth })}</td>
                  <td className="px-5 py-3 text-right text-sm font-medium text-gray-900">{fmt(totalDue)}</td>
                </tr>
                {previousPending > 0 && (
                  <tr className="bg-amber-50/50">
                    <td className="px-5 py-3 text-sm text-amber-700">{t('owner.previousPendingBalance', 'Previous Pending Balance (carried forward)')}</td>
                    <td className="px-5 py-3 text-right text-sm font-medium text-amber-700">{fmt(previousPending)}</td>
                  </tr>
                )}
                <tr className="bg-gray-50">
                  <td className="px-5 py-3 text-sm font-semibold text-gray-800">{t('owner.totalAmountDue', 'Total Amount Due')}</td>
                  <td className="px-5 py-3 text-right text-sm font-bold text-gray-900">{fmt(totalDue + previousPending)}</td>
                </tr>
                <tr>
                  <td className="px-5 py-3 text-sm text-green-700">{t('rent.amountPaid')}</td>
                  <td className="px-5 py-3 text-right text-sm font-medium text-green-700">– {fmt(paidAmount)}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr className={`border-t-2 ${remaining > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                  <td className="px-5 py-4 text-sm font-bold text-gray-900">{t('owner.remainingAmount', 'Remaining Amount')}</td>
                  <td className={`px-5 py-4 text-right text-lg font-bold ${remaining > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {fmt(remaining)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Status */}
          <div className="flex items-center gap-2 mb-8">
            <span className="text-sm text-gray-500">{t('owner.paymentStatus', 'Payment Status')}:</span>
            <span className={`px-3 py-0.5 rounded-full text-xs font-bold ${statusColors[current.status] ?? 'bg-gray-100 text-gray-600'}`}>
              {current.status}
            </span>
          </div>

          {/* Signature section */}
          <div className="grid grid-cols-2 gap-8 pt-6 border-t border-gray-100">
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-3">{t('owner.tenantSignature', 'Tenant Signature')}</p>
              <input
                type="text"
                value={signature}
                onChange={e => setSignature(e.target.value)}
                placeholder={t('owner.tenantNameSignature', 'Tenant name / signature')}
                className="w-full border-b border-gray-400 py-1 text-sm text-gray-700 bg-transparent outline-none placeholder:text-gray-300"
              />
              <p className="text-xs text-gray-400 mt-2">{t('owner.tenantNameAndSignature', 'Tenant Name & Signature')}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-3">{t('owner.authorizedBy', 'Authorized By')}</p>
              <div className="border-b border-gray-400 py-1 min-h-[28px]" />
              <p className="text-xs text-gray-400 mt-2">{t('owner.managerSignatureSeal', 'Manager Signature & Seal')}</p>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-8 pt-5 border-t border-gray-100 text-center">
            <p className="text-xs text-gray-400">
              {t('owner.computerGeneratedReceipt', 'This is a computer-generated receipt · {{hostelName}}', { hostelName })}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">
              {t('owner.generatedOn', 'Generated on {{date}}', { date: new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) })}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
